import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Typography, Box, Button } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getFunnelProgressionData } from '../api';
import ChartCard from './common/ChartCard';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';

const FunnelChart = () => {
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  const fetchFunnelData = async () => {
    try {
      setLoading(true);
      const data = await getFunnelProgressionData();
      console.log('Funnel data received:', data);
      setFunnelData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching funnel progression data:', err);
      setError('Failed to load funnel progression data. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunnelData();
  }, []);

  const handleRetry = () => {
    fetchFunnelData();
  };

  if (loading) return <LoadingBlock height={360} />;

  if (error) return <ErrorBlock message={error} onRetry={handleRetry} />;

  if (!funnelData || funnelData.length === 0) {
    return (
      <ChartCard title="User Profile Completion Funnel">
        <Typography variant="body2" align="center" sx={{ p: 3 }}>
          No funnel data available
        </Typography>
      </ChartCard>
    );
  }

  // Filter out any stages with errors if they exist
  const validFunnelData = funnelData.filter(item => !item.error);

  // Compute suggested max with headroom for end labels
  const rawCounts = validFunnelData.map(item => item.count);
  const maxValue = Math.max(...rawCounts, 0);
  const suggestedMax = Math.ceil(maxValue * 1.15);

  // Process data for chart
  const primary = theme.palette.primary.main;
  const chartData = {
    labels: validFunnelData.map(item => item.stage),
    datasets: [
      {
        label: 'Users',
        data: validFunnelData.map(item => item.count),
        backgroundColor: validFunnelData.map(() => alpha(primary, 0.6)),
        borderColor: validFunnelData.map(() => primary),
        borderWidth: 1,
      },
    ],
  };

  // Get total users count for percentage context
  const totalUsers = Math.max(...validFunnelData.map(item => item.count));

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    layout: { padding: { right: 16 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = validFunnelData[context.dataIndex].percentage;
            return [`Users: ${value.toLocaleString()}`, `Completion: ${percentage}%`];
          }
        }
      },
      datalabels: {
        // draw labels near the right end but within the chart area
        anchor: 'end',
        align: 'right',
        offset: 4,
        clamp: true,
        clip: false,
        formatter: (value, ctx) => {
          const pct = validFunnelData[ctx.dataIndex].percentage;
          return `${pct}%`;
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        suggestedMax,
        ticks: {
          precision: 0,
          callback: function(value) {
            if (value >= 1000) return value / 1000 + 'k';
            return value;
          }
        }
      },
      y: {
        ticks: { autoSkip: false }
      }
    }
  };

  return (
    <ChartCard
      title="User Profile Completion Funnel"
      actions={(
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            Total Users: {totalUsers.toLocaleString()}
          </Typography>
          <Button variant="outlined" size="small" onClick={handleRetry}>Refresh</Button>
        </Box>
      )}
    >
      <Bar data={chartData} options={chartOptions} />
    </ChartCard>
  );
};

export default FunnelChart; 